import { ReactNode, useMemo } from 'react';
import {
  DashboardOutlined,
  IdcardOutlined,
  CalendarOutlined,
  TeamOutlined,
  QuestionCircleOutlined,
  ApiOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { Result } from 'antd';
import { Link, useLocation } from 'react-router';
import { useMsal } from '@azure/msal-react';
import { AdminLayout, Wordmark, CapabilityProvider } from '@viq-admin/ui';
import type { AdminNavItem } from '@viq-admin/ui';
import { Role, useRoleClaim } from '../auth/useRoleClaim';
import { SIGNED_OUT_KEY } from '../auth/signedOutMarker';
import { displayName } from '../labels/display';
import { buildPortalCan } from '../capability/portalCapabilities';

/**
 * Customer-portal shell: the shared @viq-admin/ui AdminLayout (same chrome AND
 * behavior as the super-admin console) parameterized for this plane — role-
 * filtered nav, client-side routing via renderNavLink, and identity + sign-out
 * via the shared account dropdown. No badge (customer panel, not "- Console").
 */
type NavDef = {
  key: string;
  label: string;
  to: string;
  icon: ReactNode;
  allow: Role[];
};

const NAV: NavDef[] = [
  { key: 'live-feed', label: 'Live feed', to: '/live-feed', icon: <DashboardOutlined />, allow: ['company_admin'] },
  { key: 'batch', label: 'My Leads', to: '/batch', icon: <IdcardOutlined />, allow: ['company_admin', 'stakeholder'] },
  { key: 'conferences', label: 'Conferences', to: '/conferences', icon: <CalendarOutlined />, allow: ['company_admin'] },
  { key: 'questions', label: 'Questions Inventory', to: '/questions', icon: <QuestionCircleOutlined />, allow: ['company_admin'] },
  { key: 'users', label: 'Users', to: '/users', icon: <TeamOutlined />, allow: ['company_admin'] },
  { key: 'integrations', label: 'Integrations', to: '/integrations', icon: <ApiOutlined />, allow: ['company_admin'] },
  { key: 'settings', label: 'Company settings', to: '/settings', icon: <SettingOutlined />, allow: ['company_admin'] },
];

export function AppShell({ children }: { children: ReactNode }) {
  const { role, name, email } = useRoleClaim();
  const { instance } = useMsal();
  // Subscribe to location so the shell re-renders on client-side nav and the
  // active-item highlight tracks the clicked tab (AdminLayout sits above the
  // <Outlet>, so it can't derive this reactively on its own).
  const location = useLocation();

  const navItems: AdminNavItem[] = NAV.filter(
    (item) => role && item.allow.includes(role),
  ).map((item) => ({ key: item.key, label: item.label, href: item.to, icon: item.icon }));

  // Longest-prefix match so /conferences/abc still highlights "Conferences".
  const selectedKey = navItems
    .filter((n) => location.pathname === n.href || location.pathname.startsWith(`${n.href}/`))
    .sort((a, b) => (b.href?.length ?? 0) - (a.href?.length ?? 0))[0]?.key;

  const capabilityValue = useMemo(
    () => ({
      can: buildPortalCan(role),
      renderDenied: () => (
        <Result
          status="403"
          title="Not authorized"
          subTitle="Your account doesn't have permission for this view."
        />
      ),
    }),
    [role],
  );

  return (
    <CapabilityProvider value={capabilityValue}>
      <AdminLayout
        brand={<Wordmark />}
        navItems={navItems}
        renderNavLink={(item, children) => <Link to={item.href}>{children}</Link>}
        selectedKey={selectedKey}
        user={{
          name: name ?? undefined,
          email: email ?? undefined,
          roleLabel: role ? displayName('role', role) : undefined,
        }}
        onSignOut={() => {
          // Read by App.tsx's `signedOut` gate — without it, landing back at
          // postLogoutRedirectUri (this same origin) re-enters
          // MsalAuthenticationTemplate with zero accounts and auto-redirects
          // straight back into Entra (the sign-out loop).
          sessionStorage.setItem(SIGNED_OUT_KEY, '1');
          instance.logoutRedirect({ account: instance.getActiveAccount() ?? undefined });
        }}
      >
        {children}
      </AdminLayout>
    </CapabilityProvider>
  );
}
